// Authentication service
import BaseApiService from '../api/base.service';
import { API_ENDPOINTS, LOCAL_STORAGE_KEYS } from '@constants';
import { AuthResponse, LoginCredentials, RegisterData, User } from '@types';
import { storage } from '@utils';

class AuthService extends BaseApiService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.post<AuthResponse, LoginCredentials>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials
    );

    if (response.data.token) {
      this.setAuthData(response.data);
    }

    return response.data;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    // POST /users/register validates against registerUserSchema and rejects
    // privileged/unknown fields (e.g. role/authSubject). Send only allowed fields.
    const payload: RegisterData = {
      fullName: data.fullName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      companyName: data.companyName,
      city: data.city,
      state: data.state,
    };

    const response = await this.post<AuthResponse, RegisterData>(API_ENDPOINTS.USERS.REGISTER, payload);

    if (response.data.token) {
      this.setAuthData(response.data);
    }

    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.post(API_ENDPOINTS.AUTH.LOGOUT);
    } finally {
      this.clearAuthData();
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.get<User>(API_ENDPOINTS.AUTH.ME);
    return response.data;
  }

  async refreshToken(): Promise<string> {
    const refreshToken = storage.local.get<string>(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    const response = await this.post<{ token: string }>(API_ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    });

    if (response.data.token) {
      storage.local.set(LOCAL_STORAGE_KEYS.TOKEN, response.data.token);
    }

    return response.data.token;
  }

  private setAuthData(data: AuthResponse): void {
    storage.local.set(LOCAL_STORAGE_KEYS.TOKEN, data.token);
    storage.local.set(LOCAL_STORAGE_KEYS.USER, data.user);
    if (data.refreshToken) {
      storage.local.set(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
    }
  }

  private clearAuthData(): void {
    storage.local.remove(LOCAL_STORAGE_KEYS.TOKEN);
    storage.local.remove(LOCAL_STORAGE_KEYS.USER);
    storage.local.remove(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
  }

  getStoredUser(): User | null {
    return storage.local.get<User>(LOCAL_STORAGE_KEYS.USER);
  }

  getStoredToken(): string | null {
    return storage.local.get<string>(LOCAL_STORAGE_KEYS.TOKEN);
  }

  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }
}

export default new AuthService();
